import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import LandingView from '@/views/LandingView.vue'
import QuizAccessLayout from '@/views/quiz-access/QuizAccessLayout.vue'

function supervisorDashboardRedirect(_to: RouteLocationNormalized) {
  const auth = useAuthStore()
  if (auth.roles.includes('TRAINING_INSTRUCTOR')) return '/supervisor/dashboard/instructor'
  if (auth.roles.includes('TRAINING_LEARNER')) return '/supervisor/dashboard/learner'
  return '/supervisor/dashboard'
}

// Augment RouteMeta so TypeScript knows about our custom fields
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresGuest?: boolean
    requiresFeature?: string
    requiredRoles?: string[]
  }
}

const routes: RouteRecordRaw[] = [
  // ── Auth layout ────────────────────────────────────────────────────────────
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    meta: { requiresGuest: true },
    children: [
      { path: 'login', name: 'login', component: () => import('@/views/auth/LoginView.vue') },
      { path: 'otp', name: 'otp', component: () => import('@/views/auth/OtpView.vue') },
      { path: 'forgot-password', name: 'forgot-password', component: () => import('@/views/auth/ForgotPasswordView.vue') },
      // Password-reset-from-key: must be accessible even when logged in (email link)
      { path: 'reset-password/:key', name: 'reset-password', component: () => import('@/views/auth/ResetPasswordView.vue'), meta: { requiresGuest: false } },
    ],
  },

  // Root landing page (unauthenticated)
  {
    path: '/',
    name: 'landing',
    component: LandingView,
    meta: { requiresGuest: true },
  },

  // Quiz access flow (public)
  {
    path: '/quiz-access',
    component: QuizAccessLayout,
    meta: { requiresAuth: false },
    children: [
      {
        path: 'enter',
        name: 'quiz-access-enter',
        component: () => import('@/views/quiz-access/TinEntryView.vue'),
      },
      {
        path: 'select',
        name: 'quiz-access-select',
        component: () => import('@/views/quiz-access/CourseSelectView.vue'),
      },
      {
        path: 'quizzes',
        name: 'quiz-access-quizzes',
        component: () => import('@/views/quiz-access/QuizAccessListView.vue'),
      },
      {
        path: 'materials',
        name: 'quiz-access-materials',
        component: () => import('@/views/quiz-access/QuizAccessMaterialsView.vue'),
      },
      {
        path: 'quiz/:quizId',
        name: 'quiz-access-quiz',
        component: () => import('@/views/quiz-access/QuizAccessPlayerView.vue'),
      },
      {
        path: 'done',
        name: 'quiz-access-done',
        component: () => import('@/views/quiz-access/QuizAccessDoneView.vue'),
      },
    ],
  },

  // Certificate verify (public)
  {
    path: "/verify/:uuid",
    name: "certificate-verify",
    component: () => import("@/views/public/CertificateVerify.vue"),
    meta: { requiresAuth: false, public: true },
  },

  // Trust Center (public, product-level — no auth required)
  // The former /legal pages were removed; privacy/cookie policy now live as
  // documents in the Trust Center, and all app links point to /trust.
  {
    path: '/trust',
    name: 'trust-center',
    component: () => import('@/views/public/TrustCenterView.vue'),
    meta: { requiresAuth: false, public: true },
  },

  // ── App layout (authenticated) ─────────────────────────────────────────────
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
      },
      // Tenant admin settings — OWNER or ADMIN
      { path: 'settings', name: 'settings', component: () => import('@/views/settings/SettingsView.vue'), meta: { requiredRoles: ['OWNER', 'ADMIN'] } },
      { path: 'settings/tenant', name: 'settings-tenant', component: () => import('@/views/settings/TenantSettingsView.vue'), meta: { requiredRoles: ['OWNER', 'ADMIN'] } },
      { path: 'settings/features', name: 'settings-features', component: () => import('@/views/settings/FeaturesView.vue'), meta: { requiredRoles: ['OWNER', 'ADMIN'] } },
      { path: 'settings/billing', name: 'settings-billing', component: () => import('@/views/settings/BillingView.vue'), meta: { requiredRoles: ['OWNER', 'ADMIN'] } },
      { path: 'settings/gdpr', name: 'settings-gdpr', component: () => import('@/views/settings/GdprView.vue'), meta: { requiredRoles: ['OWNER', 'ADMIN'] } },
      { path: 'settings/api-keys', name: 'settings-api-keys', component: () => import('@/views/settings/ApiKeysView.vue'), meta: { requiredRoles: ['OWNER', 'ADMIN'] } },
      // IAM — OWNER or IAM platform role
      { path: 'iam', name: 'iam', component: () => import('@/views/iam/IamView.vue'), meta: { requiredRoles: ['OWNER', 'IAM'] } },
      { path: 'iam/matrix', name: 'iam-matrix', component: () => import('@/views/iam/RoleMatrixView.vue'), meta: { requiredRoles: ['OWNER', 'IAM'] } },
      { path: 'iam/members/:id', name: 'iam-member-detail', component: () => import('@/views/iam/MemberDetailView.vue'), meta: { requiredRoles: ['OWNER', 'IAM'] } },
      // Billing — OWNER or BILLING platform role
      { path: 'billing', name: 'billing', component: () => import('@/views/settings/BillingView.vue'), meta: { requiredRoles: ['OWNER', 'BILLING'] } },

      // Audit log — all authenticated users
      { path: 'audit', name: 'audit', component: () => import('@/views/audit/AuditView.vue') },

      // User self-service — all authenticated users
      { path: 'profile', name: 'profile', component: () => import('@/views/profile/ProfileView.vue') },
      { path: 'profile/security', name: 'profile-security', component: () => import('@/views/profile/SecurityView.vue') },
      { path: 'profile/privacy', name: 'profile-privacy', component: () => import('@/views/profile/PrivacyView.vue') },

      // ── Supervisor / HSE Training ──────────────────────────────────────────
      {
        path: 'supervisor',
        meta: { requiresFeature: 'supervisor' },
        children: [
          { path: '', redirect: supervisorDashboardRedirect },
          {
            path: 'dashboard',
            name: 'supervisor-dashboard',
            component: () => import('@/views/supervisor/dashboard/SecretaryDashboard.vue'),
          },
          {
            path: 'dashboard/instructor',
            name: 'supervisor-instructor-dashboard',
            component: () => import('@/views/supervisor/dashboard/InstructorDashboard.vue'),
          },
          {
            path: 'dashboard/learner',
            name: 'supervisor-learner-dashboard',
            component: () => import('@/views/supervisor/dashboard/LearnerDashboard.vue'),
          },
          {
            path: 'learners',
            name: 'supervisor-learners',
            component: () => import('@/views/supervisor/registry/LearnerList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'learners/:id',
            name: 'supervisor-learner-detail',
            component: () => import('@/views/supervisor/registry/LearnerDetail.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'instructors',
            name: 'supervisor-instructors',
            component: () => import('@/views/supervisor/registry/InstructorList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'instructors/:id',
            name: 'supervisor-instructor-detail',
            component: () => import('@/views/supervisor/registry/InstructorDetail.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'venues',
            name: 'supervisor-venues',
            component: () => import('@/views/supervisor/registry/VenueList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'venues/:id',
            name: 'supervisor-venue-detail',
            component: () => import('@/views/supervisor/registry/VenueDetail.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'organizations',
            name: 'supervisor-organizations',
            component: () => import('@/views/supervisor/registry/OrganizationList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'categories',
            name: 'supervisor-categories',
            component: () => import('@/views/supervisor/catalog/CategoryList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'categories/:id',
            name: 'supervisor-category-detail',
            component: () => import('@/views/supervisor/catalog/CategoryDetail.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'normative-references',
            name: 'supervisor-normative-references',
            component: () => import('@/views/supervisor/catalog/NormativeReferenceList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'module-types',
            name: 'supervisor-module-types',
            component: () => import('@/views/supervisor/config/ModuleTypeList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER'] },
          },
          {
            path: 'course-templates',
            name: 'supervisor-course-templates',
            component: () => import('@/views/supervisor/catalog/CourseTemplateList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'course-templates/:id',
            name: 'supervisor-course-template-detail',
            component: () => import('@/views/supervisor/catalog/CourseTemplateDetail.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'courses',
            name: 'supervisor-courses',
            component: () => import('@/views/supervisor/courses/CourseList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'courses/:id',
            name: 'supervisor-course-detail',
            component: () => import('@/views/supervisor/courses/CourseDetail.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'calendar',
            name: 'supervisor-calendar',
            component: () => import('@/views/supervisor/courses/CourseCalendar.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'config/document-templates',
            name: 'supervisor-document-templates',
            component: () => import('@/views/supervisor/config/DocumentTemplateList.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER'] },
          },
          {
            path: 'config/training-settings',
            name: 'supervisor-training-settings',
            component: () => import('@/views/supervisor/config/TrainingSettingsView.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER'] },
          },
          {
            path: 'expiries',
            name: 'supervisor-expiries',
            component: () => import('@/views/supervisor/expiries/ExpiryDashboard.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },
          {
            path: 'reports',
            name: 'supervisor-reports',
            component: () => import('@/views/supervisor/reports/ReportExport.vue'),
            meta: { requiredRoles: ['TRAINING_MANAGER', 'TRAINING_EDITOR'] },
          },

          // ── Learner portal ─────────────────────────────────────────────────
          {
            path: 'my-courses',
            name: 'learner-my-courses',
            component: () => import('@/views/supervisor/learner/MyCoursesView.vue'),
            meta: { requiredRoles: ['TRAINING_LEARNER'] },
          },
          {
            path: 'my-courses/:id',
            name: 'learner-course-detail',
            component: () => import('@/views/supervisor/learner/MyCourseDetailView.vue'),
            meta: { requiredRoles: ['TRAINING_LEARNER'] },
          },
          {
            path: 'my-quizzes',
            name: 'learner-my-quizzes',
            component: () => import('@/views/supervisor/assessment/MyQuizzesView.vue'),
            meta: { requiredRoles: ['TRAINING_LEARNER'] },
          },
          {
            path: 'my-quizzes/course/:courseId',
            name: 'learner-my-quizzes-course',
            component: () => import('@/views/supervisor/assessment/MyCourseQuizListView.vue'),
            meta: { requiredRoles: ['TRAINING_LEARNER'] },
          },
          {
            path: 'my-quizzes/:responseId/take',
            name: 'learner-quiz-take',
            component: () => import('@/views/supervisor/assessment/QuizPlayerView.vue'),
            meta: { requiredRoles: ['TRAINING_LEARNER'] },
          },
          {
            path: 'my-quizzes/:responseId/result',
            name: 'learner-quiz-result',
            component: () => import('@/views/supervisor/assessment/QuizResultView.vue'),
            meta: { requiredRoles: ['TRAINING_LEARNER'] },
          },

          // ── Instructor portal ──────────────────────────────────────────────
          {
            path: 'instructor/courses',
            name: 'instructor-courses',
            component: () => import('@/views/supervisor/courses/CourseList.vue'),
            meta: { requiredRoles: ['TRAINING_INSTRUCTOR'] },
          },
          {
            path: 'instructor/courses/:id',
            name: 'instructor-course-detail',
            component: () => import('@/views/supervisor/courses/CourseDetail.vue'),
            meta: { requiredRoles: ['TRAINING_INSTRUCTOR'] },
          },
        ],
      },
      // ── Agenda ─────────────────────────────────────────────────────────────────────
      {
        path: 'agenda',
        meta: { requiresFeature: 'agenda' },
        children: [
          {
            path: '',
            name: 'agenda',
            component: () => import('@/views/agenda/AgendaView.vue'),
            meta: { requiredRoles: ['CALENDAR_VIEWER', 'CALENDAR_EDITOR', 'CALENDAR_MANAGER'] },
          },
          {
            path: 'resources',
            name: 'agenda-resources',
            component: () => import('@/views/agenda/ResourceList.vue'),
            meta: { requiredRoles: ['CALENDAR_MANAGER'] },
          },
          {
            path: 'categories',
            name: 'agenda-categories',
            component: () => import('@/views/agenda/CategoryList.vue'),
            meta: { requiredRoles: ['CALENDAR_MANAGER'] },
          },
          {
            path: 'my-calendar',
            name: 'agenda-my-calendar',
            component: () => import('@/views/agenda/MyCalendarView.vue'),
            meta: { requiredRoles: ['CALENDAR_VIEWER', 'CALENDAR_EDITOR', 'CALENDAR_MANAGER'] },
          },
        ],
      },
      // ── CRM ───────────────────────────────────────────────────────────────────
      {
        path: 'crm',
        meta: { requiresFeature: 'crm' },
        children: [
          {
            path: 'clients',
            name: 'crm-clients',
            component: () => import('@/views/crm/ClientList.vue'),
            meta: { requiredRoles: ['CRM_VIEWER', 'CRM_EDITOR', 'CRM_MANAGER'] },
          },
          {
            path: 'clients/:id',
            name: 'crm-client-detail',
            component: () => import('@/views/crm/ClientDetail.vue'),
            meta: { requiredRoles: ['CRM_VIEWER', 'CRM_EDITOR', 'CRM_MANAGER'] },
          },
        ],
      },
      // ── Quoting (catalog + preventivi) ───────────────────────────────────────
      {
        path: 'quoting',
        meta: { requiresFeature: 'quoting' },
        children: [
          {
            path: 'manufacturers',
            name: 'quoting-manufacturers',
            component: () => import('@/views/catalog/manufacturers/ManufacturersListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_SALES', 'QUOTING_MANAGER'] },
          },
          {
            path: 'manufacturers/:public_id',
            name: 'quoting-manufacturer-detail',
            component: () => import('@/views/catalog/manufacturers/ManufacturerDetailView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_SALES', 'QUOTING_MANAGER'] },
          },
          {
            path: 'suppliers',
            name: 'quoting-suppliers',
            component: () => import('@/views/catalog/suppliers/SuppliersListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_SALES', 'QUOTING_MANAGER'] },
          },
          {
            path: 'suppliers/:public_id',
            name: 'quoting-supplier-detail',
            component: () => import('@/views/catalog/suppliers/SupplierDetailView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_SALES', 'QUOTING_MANAGER'] },
          },
          {
            path: 'components',
            name: 'quoting-components',
            component: () => import('@/views/catalog/components/ComponentsListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_SALES', 'QUOTING_MANAGER'] },
          },
          {
            path: 'components/:public_id',
            name: 'quoting-component-detail',
            component: () => import('@/views/catalog/components/ComponentDetailView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_SALES', 'QUOTING_MANAGER'] },
          },
          {
            path: 'boms',
            name: 'quoting-boms',
            component: () => import('@/views/catalog/boms/BomsListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
          {
            path: 'boms/:public_id',
            name: 'quoting-bom-detail',
            component: () => import('@/views/catalog/boms/BomDetailView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
          {
            path: 'system-templates',
            name: 'quoting-system-templates',
            component: () => import('@/views/catalog/system-templates/SystemTemplatesListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
          {
            path: 'system-templates/:public_id',
            name: 'quoting-system-template-detail',
            component: () => import('@/views/catalog/system-templates/SystemTemplateDetailView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
          {
            path: 'quotes',
            name: 'quoting-quotes',
            component: () => import('@/views/quoting/quotes/QuotesListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_SALES', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
          {
            path: 'quotes/:public_id',
            name: 'quoting-quote-detail',
            component: () => import('@/views/quoting/quotes/QuoteDetailView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_SALES', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
          {
            path: 'offers',
            name: 'quoting-offers',
            component: () => import('@/views/quoting/offers/OffersListView.vue'),
            meta: { requiredRoles: ['QUOTING_VIEWER', 'QUOTING_SALES', 'QUOTING_ENGINEER', 'QUOTING_MANAGER'] },
          },
        ],
      },
      // ── Help Center ───────────────────────────────────────────────────────────
      {
        path: 'help',
        children: [
          {
            path: '',
            name: 'help-center',
            component: () => import('@/views/help/HelpCenter.vue'),
          },
          {
            path: ':slug',
            name: 'help-article',
            component: () => import('@/views/help/HelpCenter.vue'),
          },
        ],
      },
    ],
  },

  // SSO bridge callback — receives JWT tokens in URL hash from oauth_complete.js
  {
    path: '/sso-callback',
    name: 'sso-callback',
    component: () => import('@/views/auth/SsoCallbackView.vue'),
    meta: { requiresAuth: false },
  },

  // ── Catch-all ──────────────────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory('/'),
  routes,
})

// ── Navigation guards ──────────────────────────────────────────────────────────
router.beforeEach((to) => {
  const auth = useAuthStore()

  // Root → landing for everyone; authenticated users redirected inside LandingView
  if (to.path === '/' && to.name !== 'landing' && !auth.isAuthenticated) {
    return { name: 'landing' }
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresGuest && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }

  // Feature guard
  if (to.meta.requiresFeature) {
    const features = auth.activeTenant?.features ?? []
    if (!features.includes(to.meta.requiresFeature as string)) {
      return { name: 'dashboard' }
    }
  }

  // Role guard — only applies when user has at least one training role
  if (to.meta.requiredRoles && to.meta.requiredRoles.length > 0) {
    // OWNER has implicit full access to all apps (mirrors backend OWNER bypass)
    if (auth.roles.includes('OWNER')) {
      return
    }
    const hasAnyRole = auth.roles.length > 0
    if (hasAnyRole) {
      const allowed = to.meta.requiredRoles.some((r) => auth.roles.includes(r))
      if (!allowed) {
        return { name: 'dashboard' }
      }
    }
    // If roles are not yet loaded (e.g. page refresh), allow through — the
    // server will enforce permissions via the API.
  }
})

export default router
